import axios from "axios";
import {
  AccountOutcome,
  AccountStatusOutcome,
  ActivateUser,
  CreateAccountOutcome,
  CreateActivateUser,
} from "../../Types/Users";

export const ACCOUNT_COPY = Object.freeze({
  createInviteSent: "User saved, invite sent",
  createFailed: "Couldn't create the user. Check the details and try again.",
  createInviteFailed: "User saved, but the invite email didn't send. Use Resend Invite.",
  resendSent: "Invite Sent",
  resendFailed: "Couldn't send the invite. Try again in a few minutes.",
  resendConfirm: "Send this account a new invite email?",
  resendInactive: "Inactive users can't be reinvited. Activate this user first.",
  resetSent: "Password Reset Sent",
  resetInvited: "No login found, invite sent",
  resetFailed: "Couldn't send the password email. Try again in a few minutes.",
  resetInactive: "This user is inactive and hasn't logged in yet. Activate them first.",
  editSaved: "User Saved",
  editFailed: "Couldn't save the user. Nothing was changed.",
  activated: "User Activated",
  activateFailed: "Couldn't activate the user. Nothing was changed.",
  deactivated: "User Deactivated",
  deactivateFailed: "Couldn't deactivate the user. Nothing was changed.",
  deleted: "User Deleted",
  deleteFailed: "Couldn't delete the user. They're still in the list.",
  invitePending: "Invite pending",
  genericFailure: "Something went wrong. Try again in a few minutes.",
});

type CopyKey = keyof typeof ACCOUNT_COPY;

// Copy for specific non-2xx statuses; anything unlisted falls back to the operation's failure copy.
type StatusCopy = Partial<Record<number, CopyKey>>;

const INACTIVE_RESEND: StatusCopy = { 409: "resendInactive" };
const INACTIVE_RESET: StatusCopy = { 409: "resetInactive" };

const baseUrl = () => import.meta.env.VITE_BACKEND_API + "/api/usermanagement";

const jsonHeaders = { headers: { "Content-Type": "application/json" } };

// The API's error text is logged for support, never shown; the UI only shows ACCOUNT_COPY.
function logFailure(operation: string, err: any) {
  const apiError = err?.response?.data?.error;
  console.error(`${operation} failed`, typeof apiError === "string" ? apiError : err);
}

function success(key: CopyKey): AccountOutcome {
  return { ok: true, message: ACCOUNT_COPY[key] };
}

function failure(key: CopyKey): AccountOutcome {
  return { ok: false, message: ACCOUNT_COPY[key] };
}

function requestFailure(
  operation: string,
  err: any,
  failKey: CopyKey,
  byStatus?: StatusCopy
): AccountOutcome {
  logFailure(operation, err);
  const status = err?.response?.status;
  return failure((typeof status === "number" && byStatus?.[status]) || failKey);
}

async function simpleAction(
  operation: string,
  request: () => Promise<unknown>,
  okKey: CopyKey,
  failKey: CopyKey,
  byStatus?: StatusCopy
): Promise<AccountOutcome> {
  try {
    await request();
    return success(okKey);
  } catch (err) {
    return requestFailure(operation, err, failKey, byStatus);
  }
}

export function trimAccountFields(fields: CreateActivateUser): CreateActivateUser {
  return {
    ...fields,
    FirstName: fields.FirstName.trim(),
    LastName: fields.LastName.trim(),
    EmailAddress: fields.EmailAddress.trim(),
  };
}

export async function createAccount(fields: CreateActivateUser): Promise<CreateAccountOutcome> {
  let body: ActivateUser & { invite?: { sent?: boolean; error?: string | null } };
  try {
    const res = await axios.post(baseUrl() + "/create/", trimAccountFields(fields), jsonHeaders);
    body = res.data;
  } catch (err) {
    logFailure("createAccount", err);
    return { created: false, message: ACCOUNT_COPY.createFailed };
  }

  if (body?.invite?.sent === true) {
    return { created: true, user: body, invite: success("createInviteSent") };
  }
  const inviteError = body?.invite?.error;
  console.error("createAccount invite failed", typeof inviteError === "string" ? inviteError : body);
  return { created: true, user: body, invite: failure("createInviteFailed") };
}

export function editAccount(userId: string, fields: CreateActivateUser): Promise<AccountOutcome> {
  return simpleAction(
    "editAccount",
    () => axios.put(baseUrl() + "/edit/" + userId, trimAccountFields(fields), jsonHeaders),
    "editSaved",
    "editFailed"
  );
}

async function requestReset(operation: string, url: string): Promise<AccountOutcome> {
  try {
    const res = await axios.post(url);
    if (res.data?.action === "reset") return success("resetSent");
    if (res.data?.action === "invited") return success("resetInvited");
    console.error(`${operation} failed`, res.data);
  } catch (err) {
    return requestFailure(operation, err, "resetFailed", INACTIVE_RESET);
  }
  return failure("resetFailed");
}

export function resetPassword(email: string): Promise<AccountOutcome> {
  return requestReset("resetPassword", baseUrl() + "/resetpassword/" + encodeURIComponent(email));
}

export function resetPasswordForUser(userId: string): Promise<AccountOutcome> {
  return requestReset("resetPasswordForUser", baseUrl() + "/" + userId + "/resetpassword");
}

export function resendInvite(userId: string): Promise<AccountOutcome> {
  return simpleAction(
    "resendInvite",
    () => axios.post(baseUrl() + "/" + userId + "/invite"),
    "resendSent",
    "resendFailed",
    INACTIVE_RESEND
  );
}

export function activateAccount(userId: string): Promise<AccountOutcome> {
  return simpleAction(
    "activateAccount",
    () => axios.put(baseUrl() + "/activate/" + userId),
    "activated",
    "activateFailed"
  );
}

export function deactivateAccount(userId: string): Promise<AccountOutcome> {
  return simpleAction(
    "deactivateAccount",
    () => axios.put(baseUrl() + "/deactivate/" + userId),
    "deactivated",
    "deactivateFailed"
  );
}

export function deleteAccount(userId: string): Promise<AccountOutcome> {
  return simpleAction(
    "deleteAccount",
    () => axios.delete(baseUrl() + "/delete/" + userId),
    "deleted",
    "deleteFailed"
  );
}

export async function getAccountStatus(userId: string): Promise<AccountStatusOutcome> {
  try {
    const res = await axios.get(baseUrl() + "/" + userId + "/auth0status");
    const { hasAuth0Account, hasLoggedIn } = res.data ?? {};
    if (typeof hasAuth0Account === "boolean" && typeof hasLoggedIn === "boolean") {
      return { ok: true, hasAuth0Account, hasLoggedIn };
    }
    console.error("getAccountStatus failed", res.data);
  } catch (err) {
    logFailure("getAccountStatus", err);
  }
  return { ok: false };
}
